import Facade from '../../Facade'
import * as PIXI from 'pixi.js'
import { ICustomEventData } from '@modules/src/interfaces/IComponents'
import { ButtonState } from '@modules/src/enums/ButonStates'
import { getStakeButtonsState, syncPanelButtonEnabledState } from '@modules/src/panel/PanelUtils'
import { TranslationUtils } from '@modules/src/utils/TranslationUtils'
import { toCurrency } from '@/ui/currency'
import {
    BUY_BONUS_CAPTION_STYLES,
    BUY_BONUS_CONFIRM_LABEL_STYLES,
    BUY_BONUS_LABEL_STYLE,
    WORD_ART_SHADOW_DISTANCE,
} from '@/containers/common/TextStyles'
import { OutlinedTextLabel } from '@/containers/common/OutlinedTextLabel'
import { ScalingTextField } from '@/containers/common/ScalingTextField'
import gsap from 'gsap'

// --- Bet calculation ---

export const getCurrentBetAmount = (): number => Number(Facade.model?.currentBet || 0)

export const getCurrentCoinBetAmount = (): number => {
    const currentBet = getCurrentBetAmount()
    if (!Number.isFinite(currentBet) || currentBet < 0) {
        return 0
    }

    return currentBet / 100
}

export const getBuyBonusMultiplier = (): number => {
    return 80
}

export const getCurrentBuyBonusTotalAmount = (): number => {
    const currentCoinBet = getCurrentCoinBetAmount()
    return currentCoinBet * getBuyBonusMultiplier()
}

/**
 * Buy Bonus is switched off for this session, either by the operator config or by the server
 * (game.misc.buy_bonus = 0, folded into the operator flags when the init response is parsed).
 */
export const isBuyBonusHidden = (): boolean => {
    const operatorFlags = ((Facade as any).operatorFeatureConfig || {}) as Record<string, any>
    const defaults = ((Facade as any).defaultConfig || {}) as Record<string, any>

    return operatorFlags.hideBuyBonus === true || defaults.hideBuyBonus === true
}

//## --- Balance / affordability ---
export const isBuyBonusUnaffordable = (): boolean => {
    const balance = Number(Facade.model?.balance ?? 0)
    const cost = getCurrentBuyBonusTotalAmount()

    if (!Number.isFinite(balance) || !Number.isFinite(cost) || cost <= 0) {
        return false
    }

    return balance + 1e-9 < cost
}

/** Balance and buy-total are both major currency units (same as UI balance). */
export const canAffordBuyBonus = (): boolean => {
    const balance = Number(Facade.model?.balance ?? 0)
    const cost = getCurrentBuyBonusTotalAmount()
    if (!Number.isFinite(balance) || !Number.isFinite(cost) || cost <= 0) {
        return false
    }
    return balance + 1e-9 >= cost
}

// --- Amount rendering ---

export const formatAmountForSprites = (amount: number): string => {
    const safeAmount = Number.isFinite(amount) ? amount : 0
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: true,
    }).format(safeAmount)
}

/** Operator currency mapped to a display symbol — same helper the jackpot ladders use. */
export const getCurrencySymbol = (): string =>
    toCurrency(String(Facade.model?.initData?.player?.currency || '').trim())

const BUY_BONUS_VALUE_MAX_WIDTH = 360

//## Max Width for the bet and total value fields in the Buy Bonus popup.
export const BUY_BONUS_BET_VALUE_MAX_WIDTH = 350
export const BUY_BONUS_TOTAL_VALUE_MAX_WIDTH = 460

/**
 * Bet / total value display for the Buy Bonus popup.
 *
 * Symbol and amount are one text object. The bitmap font this replaced only carried
 * `0-9 , . $ € £ R K`, so any currency outside that set — AUD's `A$`, `GC`, `₹`, `zł` — had to be
 * drawn as a separate PIXI.Text beside the bitmap digits, in a different face and a flat white
 * with no gradient. A single text object cannot mismatch itself.
 *
 * Wrapped in a ScalingTextField because `maxWidth` is not a PixiJS v8 text property — the field's
 * own constructor argument is the only thing that actually clamps.
 */
export class BuyBonusValueField extends PIXI.Container {
    private readonly amountText: ScalingTextField

    constructor(maxWidth: number = BUY_BONUS_VALUE_MAX_WIDTH) {
        super()

        this.amountText = new ScalingTextField(
            1,
            maxWidth,
            PIXI.Text as any,
            BUY_BONUS_LABEL_STYLE
        )
        this.addChild(this.amountText)
    }

    setAmount(amount: number): void {
        const symbol = getCurrencySymbol()
        //## Letter codes read as words and need the space ("GC 10.00"); glyph symbols sit tight
        //## against the digits ("A$10.00", "$10.00").
        const separator = /[A-Za-z]$/.test(symbol) ? ' ' : ''

        this.amountText.text = `${symbol}${separator}${formatAmountForSprites(amount)}`
    }
}

export const renderBuyBonusValue = (element: unknown, amount: number): void => {
    if (element instanceof BuyBonusValueField) element.setAmount(amount)
}

const BUY_BONUS_INSUFFICIENT_TEXT_FALLBACK = 'INSUFFICIENT BALANCE'
const BUY_BONUS_INSUFFICIENT_FADE_IN_S = 0.25

const resolveInsufficientBalanceText = (): string => {
    const translated = TranslationUtils.translate('INSUFFICIENT BALANCE')
    return translated === 'INSUFFICIENT BALANCE' ? BUY_BONUS_INSUFFICIENT_TEXT_FALLBACK : translated
}

export const syncBuyBonusInsufficientBalance = (data: ICustomEventData): void => {
    const element = data.component?.componentElement as PIXI.Text | undefined
    if (!element) {
        return
    }

    const label = resolveInsufficientBalanceText()
    if (element.text !== label) {
        data.component.setText(label)
        element.anchor?.set?.(0.5)
    }

    if (!isBuyBonusUnaffordable()) {
        gsap.killTweensOf(element)
        element.visible = false
        element.alpha = 0
        return
    }

    if (element.visible) {
        return
    }

    gsap.killTweensOf(element)
    element.visible = true
    element.alpha = 0
    gsap.to(element, { alpha: 1, duration: BUY_BONUS_INSUFFICIENT_FADE_IN_S, ease: 'power1.out' })
}

export const hideBuyBonusInsufficientBalance = (data: ICustomEventData): void => {
    const element = data.component?.componentElement as PIXI.Text | undefined
    if (!element) {
        return
    }

    gsap.killTweensOf(element)
    element.visible = false
    element.alpha = 0
}

//## --------------------------- Popup button state -----------------------------------------------

//## Get Stake Limits..!
const getStakeLimits = () => getStakeButtonsState({ facade: Facade } as any)

const applyButtonState = (data: ICustomEventData, enabled: boolean, normal: string, disabled: string): void => {
    syncPanelButtonEnabledState(data, enabled, normal, disabled)

    const button = data.component.componentElement as PIXI.Sprite | undefined
    if (button) button.alpha = 1
}

export const syncBuyBonusMinusButton = (data: ICustomEventData): void =>
    applyButtonState(data, getStakeLimits().canDecrease, 'minus_button_normal.png', 'minus_button_disabled.png')

export const syncBuyBonusPlusButton = (data: ICustomEventData): void =>
    applyButtonState(data, getStakeLimits().canIncrease, 'plus_button_normal.png', 'plus_button_disabled.png')

/** Always-enable helper (e.g. close button). Not balance-gated. */
export const enableBuyBonusButton = (data: ICustomEventData): void => {
    const button = data.component.componentElement as any
    if (!button) return

    button.eventMode = 'static'
    button.interactive = true
    button.cursor = 'pointer'
}

const BUY_CONFIRM_TEXTURES = {
    normal: 'buttonNormal.png',
    disabled: 'buttonDisabled.png',
}

/**
 * `buttonNormal.png` is 698×341 with transparent padding / soft shadow around the gold frame.
 * Default sprite bounds therefore over-accept clicks outside the visible Buy button.
 * Insets measured from solid art (alpha≥200, not near-black) on the 1× asset; ratios keep
 * @0.5x atlases aligned.
 */
const BUY_CONFIRM_HIT_INSETS = {
    left: 17 / 698,
    top: 0 / 341,
    right: 49 / 698,
    bottom: 67 / 341,
} as const
const BUY_CONFIRM_CORNER_RADIUS = 35 / 698

/** Clamp confirm-button hit area to the visible gold frame (works for 1× and @0.5x textures). */
export const applyBuyBonusConfirmHitArea = (data: ICustomEventData): void => {
    const element = data.component?.componentElement as PIXI.Sprite | undefined
    const texture = element?.texture
    if (!element || !texture?.width || !texture?.height) return

    const tw = texture.width
    const th = texture.height
    const ax = element.anchor?.x ?? 0
    const ay = element.anchor?.y ?? 0

    const width = tw * (1 - BUY_CONFIRM_HIT_INSETS.left - BUY_CONFIRM_HIT_INSETS.right)
    const height = th * (1 - BUY_CONFIRM_HIT_INSETS.top - BUY_CONFIRM_HIT_INSETS.bottom)
    const x = tw * BUY_CONFIRM_HIT_INSETS.left - tw * ax
    const y = th * BUY_CONFIRM_HIT_INSETS.top - th * ay

    element.hitArea = new PIXI.RoundedRectangle(x, y, width, height, tw * BUY_CONFIRM_CORNER_RADIUS)
}

//## --------------------------- Confirm button label ---------------------------------------------

const CONFIRM_LABEL_TRANSLATION_KEY = 'BUY'

/** `buytextNormal.png` is drawn at 85% opacity; hover and down are the very same art at full. */
const CONFIRM_LABEL_IDLE_ALPHA = 0.851

/**
 * Where the sprite put its glyphs inside its 356x164 frame: horizontally centred, and 19px above the
 * frame's middle because the rest of the height is drop-shadow padding. The buy-bonus factory lays
 * these items out from their top-left corner (it never reads the `anchor` setting), so this container
 * keeps the same corner as its origin and the word lands exactly where the art did.
 */
const CONFIRM_LABEL_GLYPH_CENTER = { x: 177.5, y: 63 }

/** Keeps the longer translations (ACQUISTA, ACHETER) inside the button's 574px-wide glass panel. */
const CONFIRM_LABEL_MAX_WIDTH = 480

/**
 * The word on the Buy Bonus confirm button, typed instead of drawn.
 *
 * The atlas only carries an English "BUY", which cannot serve en_sc ("PLAY") or any other locale, so
 * the four `buytext*.png` states are reproduced here: gold gradient when the player can afford the
 * purchase, silver when they cannot, and full opacity while the button is hovered or held.
 */
export class BuyBonusConfirmLabel extends PIXI.Container {
    private readonly word: OutlinedTextLabel
    private enabled: boolean | null = null

    constructor() {
        super()

        this.word = new OutlinedTextLabel(BUY_BONUS_CONFIRM_LABEL_STYLES.enabled, {
            maxWidth: CONFIRM_LABEL_MAX_WIDTH,
            shadowDistance: WORD_ART_SHADOW_DISTANCE,
        })
        this.word.position.set(CONFIRM_LABEL_GLYPH_CENTER.x, CONFIRM_LABEL_GLYPH_CENTER.y)
        this.addChild(this.word)

        this.setState(false)
    }

    /**
     * @param enabled     player can afford the purchase — gold rather than silver.
     * @param highlighted pointer is over or holding the button.
     */
    setState(enabled: boolean, highlighted: boolean = false): void {
        if (this.enabled !== enabled) {
            this.enabled = enabled
            this.word.setPalette(
                enabled ? BUY_BONUS_CONFIRM_LABEL_STYLES.enabled : BUY_BONUS_CONFIRM_LABEL_STYLES.disabled
            )
        }

        // Re-read on every state change: the popup is built long before the dictionaries land.
        this.word.text = TranslationUtils.translate(CONFIRM_LABEL_TRANSLATION_KEY)

        this.alpha = enabled && !highlighted ? CONFIRM_LABEL_IDLE_ALPHA : 1
    }
}

//## --------------------------- Message box captions ---------------------------------------------

/**
 * The spine's BUY and PLAY skins letter "BET PER SPIN" and "TOTAL BET" into the message box, so every
 * other locale needs them typed instead — see `hideBuyBonusSpineCaptions` for the other half of this.
 *
 * Positions are the spine attachments' own, and so is the scale the popup gives the animation, which
 * lets the captions be built at the size the art was drawn (a 63px cap) and land exactly where it sat.
 */
export const BUY_BONUS_CAPTION_KEYS = {
    betPerSpin: 'BET PER SPIN',
    totalBet: 'TOTAL BET',
} as const

/** Wider than the 516px the English art measures, so long translations shrink rather than overflow. */
const CAPTION_MAX_WIDTH = 700

/** Roboto Black sets these captions 14% wider than the condensed face the art uses. */
const CAPTION_SQUEEZE_X = 0.875

/** Depth of the extruded bottom face under the lettering. */
const CAPTION_BEVEL_OFFSET_Y = 7

export const createBuyBonusCaption = (): OutlinedTextLabel =>
    new OutlinedTextLabel(BUY_BONUS_CAPTION_STYLES, {
        maxWidth: CAPTION_MAX_WIDTH,
        shadowDistance: WORD_ART_SHADOW_DISTANCE,
        squeezeX: CAPTION_SQUEEZE_X,
        bevelOffsetY: CAPTION_BEVEL_OFFSET_Y,
    })

/** The art is all caps; a few dictionaries answer in title case. */
export const renderBuyBonusCaption = (data: ICustomEventData, key: string): void => {
    const label = data.component?.componentElement
    if (label instanceof OutlinedTextLabel) {
        label.text = TranslationUtils.translate(key).toUpperCase()
    }
}

let confirmLabel: BuyBonusConfirmLabel | null = null

export const captureBuyBonusLabel = (data: ICustomEventData): void => {
    const element = data.component?.componentElement
    if (!(element instanceof BuyBonusConfirmLabel)) {
        return
    }

    confirmLabel = element
    confirmLabel.setState(canAffordBuyBonus())
}

export const syncBuyBonusConfirmButton = (data: ICustomEventData): void => {
    const enabled = canAffordBuyBonus()
    applyButtonState(data, enabled, BUY_CONFIRM_TEXTURES.normal, BUY_CONFIRM_TEXTURES.disabled)
    applyBuyBonusConfirmHitArea(data)

    confirmLabel?.setState(enabled)
}

export const initBuyBonusConfirmButton = (data: ICustomEventData): void => {
    syncBuyBonusConfirmButton(data)

    const button = data.component.componentElement as any
    if (!button || button.__dgBuyLabelBound) return

    button.__dgBuyLabelBound = true

    const highlight = (highlighted: boolean) => () => confirmLabel?.setState(canAffordBuyBonus(), highlighted)

    button.on(ButtonState.POINTER_OVER, highlight(true))
    button.on(ButtonState.POINTER_DOWN, highlight(true))
    button.on(ButtonState.POINTER_OUT, highlight(false))
    button.on(ButtonState.POINTER_UP, highlight(false))
    button.on(ButtonState.POINTER_UP_OUT, highlight(false))
}
